import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserDisputes, getDisputeStats } from '../../services/disputeService';
import { Dispute, DisputeStats, DisputeStatus } from '../../types/dispute';
import './AdminDisputesScreen.css';

const AdminDisputesScreen: React.FC = () => {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [filteredDisputes, setFilteredDisputes] = useState<Dispute[]>([]);
  const [disputeStats, setDisputeStats] = useState<DisputeStats>({
    open: 0,
    underReview: 0,
    resolved: 0
  });
  const [filter, setFilter] = useState<DisputeStatus | 'all'>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch disputes
        const disputesData = await getUserDisputes() as any;
        setDisputes(disputesData);
        setFilteredDisputes(disputesData);
        
        // Fetch dispute statistics
        const stats = await getDisputeStats() as any;
        setDisputeStats(stats);
      } catch (err: any) {
        setError(err.message || 'Failed to load disputes');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Apply filter when filter state changes
  useEffect(() => {
    if (filter === 'all') {
      setFilteredDisputes(disputes);
    } else {
      setFilteredDisputes(disputes.filter(dispute => dispute.status === filter));
    }
  }, [filter, disputes]);
  
  const handleFilterChange = (newFilter: DisputeStatus | 'all') => {
    setFilter(newFilter);
  };
  
  const handleViewDispute = (disputeId: string) => {
    navigate(`/admin/disputes/${disputeId}`);
  };
  
  if (loading) {
    return (
      <div className="admin-disputes-container">
        <div className="loading-state">
          <h2>Loading disputes...</h2>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="admin-disputes-container">
        <div className="error-state">
          <h2>Error Loading Disputes</h2>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-disputes-container">
      <div className="admin-disputes-header">
        <h1>Dispute Management</h1>
      </div>
      
      <div className="dispute-stats">
        <div className="stat-card">
          <h3>Open</h3>
          <div className="stat-value">{disputeStats.open}</div>
        </div>
        <div className="stat-card">
          <h3>Under Review</h3>
          <div className="stat-value">{disputeStats.underReview}</div>
        </div>
        <div className="stat-card">
          <h3>Resolved</h3>
          <div className="stat-value">{disputeStats.resolved}</div>
        </div>
      </div>
      
      <div className="filter-controls">
        <button 
          className={`filter-button ${filter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
        </button>
        <button 
          className={`filter-button ${filter === DisputeStatus.Open ? 'active' : ''}`}
          onClick={() => handleFilterChange(DisputeStatus.Open)}
        >
          Open
        </button>
        <button 
          className={`filter-button ${filter === DisputeStatus.UnderReview ? 'active' : ''}`}
          onClick={() => handleFilterChange(DisputeStatus.UnderReview)}
        >
          Under Review
        </button>
        <button 
          className={`filter-button ${filter === DisputeStatus.Resolved ? 'active' : ''}`}
          onClick={() => handleFilterChange(DisputeStatus.Resolved)}
        >
          Resolved
        </button>
      </div>
      
      {filteredDisputes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" width="48" height="48" stroke="#6c757d" fill="none">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3>No disputes found</h3>
          <p>There are no disputes matching the selected filter.</p>
        </div>
      ) : (
        <table className="disputes-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Transaction</th>
              <th>Initiated By</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDisputes.map(dispute => (
              <tr key={dispute.id}>
                <td>{dispute.id}</td>
                <td>
                  <Link to={`/admin/transactions/${dispute.transactionId}`}>
                    {dispute.transactionId}
                  </Link>
                </td>
                <td>{dispute.initiatedBy}</td>
                <td>
                  <span className={`status-badge status-${dispute.status}`}>
                    {dispute.status}
                  </span>
                </td>
                <td>{new Date(dispute.createdAt).toLocaleString()}</td>
                <td>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => handleViewDispute(dispute.id)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminDisputesScreen;